import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Play, ListChecks, CheckCircle2, SkipForward, ArrowRight, ClipboardList } from "lucide-react";

const slides = [
  {
    icon: ClipboardList,
    title: "Daily Task Limit",
    text: "You have a set number of tasks each day. The admin decides the limit. Focus on quality, not speed. Complete them one by one.",
  },
  {
    icon: ListChecks,
    title: "Your Todo List",
    text: "Each morning after punch-in, you’ll see your day’s tasks. Tap Start to begin working on one. You must finish the full list before other work.",
  },
  {
    icon: CheckCircle2,
    title: "Mark & Confirm",
    text: "When you finish a task, mark it complete. After all tasks are done, review the list and confirm each one is correct. Only then is your day truly done.",
  },
  {
    icon: SkipForward,
    title: "Already Know This?",
    text: "You can skip this guide anytime. If you understand how tasks work, just press Skip and jump straight to your list.",
  },
];

export default function OnboardingSlides({ open, onClose }) {
  const [index, setIndex] = useState(0);

  if (!open) return null;

  const SlideIcon = slides[index].icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="onboarding-modal">
      <AnimatePresence mode="wait">
        <motion.div
          key={index}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.25 }}
          className="w-full max-w-lg px-4"
        >
          <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
            <CardContent className="p-8 space-y-6">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-[#132018] border border-[#21362A] flex items-center justify-center">
                  <SlideIcon className="w-7 h-7 text-[#D4AF37]" />
                </div>
              </div>

              <div className="text-center space-y-2">
                <h2 className="font-display text-2xl text-white">{slides[index].title}</h2>
                <p className="text-[#A1B4A8] text-sm leading-relaxed">{slides[index].text}</p>
              </div>

              <div className="flex justify-center gap-2">
                {slides.map((_, i) => (
                  <div
                    key={i}
                    className={`h-1.5 rounded-full transition-all ${i === index ? "w-8 bg-[#D4AF37]" : "w-2 bg-[#21362A]"}`}
                  />
                ))}
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="ghost"
                  className="text-[#A1B4A8] hover:text-white"
                  onClick={() => { setIndex(0); onClose(true); }}
                  data-testid="onboarding-skip-btn"
                >
                  <SkipForward className="w-4 h-4 mr-2" /> Skip
                </Button>
                <div className="flex-1" />
                {index > 0 && (
                  <Button
                    variant="outline"
                    className="border-[#21362A] text-[#A1B4A8] hover:text-white"
                    onClick={() => setIndex(i => i - 1)}
                    data-testid="onboarding-prev-btn"
                  >
                    Back
                  </Button>
                )}
                {index < slides.length - 1 ? (
                  <Button
                    className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
                    onClick={() => setIndex(i => i + 1)}
                    data-testid="onboarding-next-btn"
                  >
                    Next <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
                    onClick={() => { setIndex(0); onClose(true); }}
                    data-testid="onboarding-done-btn"
                  >
                    <Play className="w-4 h-4 mr-2" /> Start My Day
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
